import { useState } from "react";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  title: string;
  subtitle: string;
  items: FAQItem[];
}

export default function FAQSection({ title, subtitle, items }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <section className="w-full py-16 md:py-20 bg-background-100">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            {title}
          </h2>
          <p className="text-sm text-foreground-600">
            {subtitle}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {items.map((item, index) => (
            <div
              key={index}
              className="rounded-xl border border-background-200/70 bg-background-50 overflow-hidden"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer"
              >
                <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 pr-4">
                  {item.question}
                </h3>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${
                  openIndex === index ? "rotate-45 bg-primary-100 text-primary-600" : "bg-background-200 text-foreground-500"
                }`}>
                  <i className="ri-add-line text-sm"></i>
                </div>
              </button>

              <div
                className={`px-6 transition-all duration-300 overflow-hidden ${
                  openIndex === index ? "pb-5 max-h-96" : "max-h-0"
                }`}
              >
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {item.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}